import type { IntervalResult, TrackingOptions, TrackingStrategies } from "../contracts";
import { DEFAULT_TRACKING_OPTIONS } from "../options";
import type { FrameRecoveryProvider } from "../recovery/contracts";

/**
 * Finished interval results, retained across session lifetimes.
 *
 * A `TrackingSession` lives as long as the component that created it, so
 * anything that unmounts the player — switching to another asset in a carousel,
 * collapsing the output, remounting for any reason — used to throw away every
 * completed interval and recompute it on the way back. Decoding and tracking a
 * video again to reach results already derived is pure waste.
 *
 * What is cached is small: `IntervalResult.decode` is deliberately
 * `Omit<IntervalFrames, "frames">`, so no pixels are retained — only frame
 * predictions (boxes, confidences, diagnostics) and interval metadata.
 *
 * Correctness rests on the key:
 *
 * - **Interval id** is `JSON.stringify([trackId, start, end])`, where the
 *   anchors carry their coordinates, so an edited anchor yields a different id
 *   and can never read a stale entry.
 * - **Source and options** separate videos, strategies and nano modes, which
 *   all produce different results for the same interval.
 * - **Rerun token** (`runKey`) separates an explicit rerun, which exists to
 *   discard and recompute.
 * - **Recovery provider key** is documented "stable for one original output run
 *   and video", and carries the parent run uuid, model and prompt version.
 *   Including it means model-assisted frames are never reused by a later run,
 *   while switching assets inside one run stays warm.
 *
 * Only completed intervals are stored. Provisional sparse previews are
 * published before fusion finishes and would otherwise be resurrected as though
 * they were final.
 */

/**
 * Bounded on retained frames rather than entries, because entries vary by two
 * orders of magnitude: an interval holds up to `maxIntervalFrames` frame
 * predictions, each carrying its own box, two nested per-method predictions and
 * their diagnostics. Counting entries would bound 1500 of those at anywhere
 * from a few MB to a few hundred.
 */
const MAX_FRAMES = 200_000;

/** Insertion-ordered, so the oldest key is the first — a Map is enough for LRU-by-age. */
const results = new Map<string, IntervalResult>();
let retainedFrames = 0;

/** Key-order-independent, so an options object cannot hash two ways. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : 1));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
}

/**
 * Every option is part of the key, not an enumerated subset. Anything that can
 * change a prediction — strategies and nano mode today, ROI size or a model URL
 * the day either becomes configurable — separates results by construction,
 * rather than silently returning boxes computed under different settings.
 */
function sourceKey(scope: ResultCacheScope): string {
  // Segments rather than one array, so a discard can match a leading run of
  // them. Each is JSON, which cannot contain a raw newline, so the separator
  // is unambiguous.
  return `${JSON.stringify(scope.src ?? "")}\n${stableStringify(scope.options)}`;
}

function scopeKey(scope: ResultCacheScope): string {
  return `${sourceKey(scope)}\n${JSON.stringify(scope.recoveryKey ?? "none")}`;
}

export interface ResultCacheScope {
  src: string | undefined;
  options: TrackingOptions;
  recoveryKey: string | undefined;
  /**
   * Separates one run's results from the next. It is per-player state that
   * resets when the player remounts, so it cannot be what makes a rerun stick —
   * {@link forgetScope} is. It still keeps a rerun's results apart in the cache
   * for the life of the mount that produced them.
   */
  runKey?: number;
}

/** What a player or a warmer is configured with, before either has run. */
export interface TrackingScopeInput {
  src: string | undefined;
  strategies?: TrackingStrategies;
  nanoMode?: TrackingOptions["nanoMode"];
  nanoModelUrls?: TrackingOptions["nanoModelUrls"];
  runKey?: number;
  recoveryEnabled?: boolean;
  recoveryProvider?: FrameRecoveryProvider;
}

/** The options a given configuration runs under. */
export function trackingOptionsFor(
  input: Pick<TrackingScopeInput, "strategies" | "nanoMode" | "nanoModelUrls">,
): TrackingOptions {
  return {
    ...DEFAULT_TRACKING_OPTIONS,
    strategies: input.strategies ?? DEFAULT_TRACKING_OPTIONS.strategies,
    nanoMode: input.nanoMode ?? DEFAULT_TRACKING_OPTIONS.nanoMode,
    nanoModelUrls: input.nanoModelUrls ?? DEFAULT_TRACKING_OPTIONS.nanoModelUrls,
  };
}

function entryKey(scope: ResultCacheScope, intervalId: string): string {
  // The recovery provider and rerun token are their own segments, so a discard
  // can match every run of one source and settings at once.
  return `${scopeKey(scope)}\n${scope.runKey ?? 0}\n${intervalId}`;
}

/** Remember a completed interval. Provisional results must not be passed here. */
export function rememberIntervalResult(scope: ResultCacheScope, result: IntervalResult): void {
  if (!scope.src) return;
  const key = entryKey(scope, result.interval.id);
  // Re-insert so a refreshed result counts as most recently used.
  drop(key);
  results.set(key, result);
  retainedFrames += result.frames.length;
  while (retainedFrames > MAX_FRAMES) {
    const oldest = results.keys().next();
    if (oldest.done) break;
    drop(oldest.value);
  }
}

function drop(key: string): void {
  const existing = results.get(key);
  if (!existing) return;
  retainedFrames -= existing.frames.length;
  results.delete(key);
}

/**
 * Forget everything cached for a scope. A rerun exists to discard results, and
 * the discard has to outlive this player: the rerun token is per-player state
 * that resets when the player remounts, so without this the next remount would
 * recall exactly what the rerun threw away.
 */
export function forgetScope(scope: ResultCacheScope): void {
  if (!scope.src) return;
  // Every recovery provider this source and settings produced under, not just
  // the caller's. A player discards on rerun before it has configured recovery,
  // so keying this on its provider dropped the locally tracked results and left
  // the recovery-refined ones to be recalled into the run meant to replace them.
  const prefix = `${sourceKey(scope)}\n`;
  for (const key of [...results.keys()]) {
    if (key.startsWith(prefix)) drop(key);
  }
}

/** Completed results already known for `intervalIds`, in that scope. */
export function recallIntervalResults(
  scope: ResultCacheScope,
  intervalIds: Iterable<string>,
): Map<string, IntervalResult> {
  const found = new Map<string, IntervalResult>();
  if (!scope.src) return found;
  for (const id of intervalIds) {
    const hit = results.get(entryKey(scope, id));
    if (hit) found.set(id, hit);
  }
  return found;
}

/** Test seam. */
export function clearIntervalResultCache(): void {
  results.clear();
  retainedFrames = 0;
}
